// Export Utilities - Generate PDF, Excel, Word files
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, AlignmentType, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { ExportReportData } from '../services/ExportDataService';

// Hospital branding
const HOSPITAL_NAME = 'KBR LIFE CARE HOSPITALS';
const HOSPITAL_COLOR = '#4A90E2';

// Format currency - use Rs. for better PDF compatibility with proper Indian number formatting
const formatCurrency = (amount: number): string => {
  // Format with Indian numbering system (lakhs, crores)
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  return `Rs. ${formatted}`;
};

// Format date range
const formatDateRange = (start: Date, end: Date): string => {
  return `${start.toLocaleDateString('en-GB')} to ${end.toLocaleDateString('en-GB')}`;
};

// ============================================
// PDF EXPORT
// ============================================
export const exportToPDF = async (data: ExportReportData, filters: any) => {
  try {
    console.log('📄 Generating PDF export...');
    
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;
    
    // Header with logo placeholder and hospital name
    doc.setFontSize(22);
    doc.setTextColor(HOSPITAL_COLOR);
    doc.setFont('helvetica', 'bold');
    doc.text(HOSPITAL_NAME, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text('Hospital Analytics Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${formatDateRange(data.startDate, data.endDate)}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
    doc.text(`Generated: ${data.generatedAt.toLocaleString('en-GB')}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;
    
    // Draw separator line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(14, yPos, pageWidth - 14, yPos);
    yPos += 15;
    
    // Key Metrics Cards (Visual Summary)
    if (filters.includeRevenue || filters.includePatients || filters.includeAppointments) {
      const cardWidth = (pageWidth - 42) / 3;
      const cardHeight = 25;
      let xPos = 14;
      
      // Revenue Card
      if (filters.includeRevenue) {
        doc.setFillColor(220, 252, 231);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(16, 185, 129);
        doc.setLineWidth(0.8);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'S');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('TOTAL REVENUE', xPos + cardWidth / 2, yPos + 8, { align: 'center' });
        doc.setFontSize(14);
        doc.setTextColor(16, 185, 129);
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrency(data.totalRevenue), xPos + cardWidth / 2, yPos + 18, { align: 'center' });
        xPos += cardWidth + 4;
      }
      
      // Patients Card
      if (filters.includePatients) {
        doc.setFillColor(219, 234, 254);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.8);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'S');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('TOTAL PATIENTS', xPos + cardWidth / 2, yPos + 8, { align: 'center' });
        doc.setFontSize(14);
        doc.setTextColor(59, 130, 246);
        doc.setFont('helvetica', 'bold');
        doc.text(data.totalPatients.toString(), xPos + cardWidth / 2, yPos + 18, { align: 'center' });
        xPos += cardWidth + 4;
      }
      
      // Appointments Card
      if (filters.includeAppointments) {
        doc.setFillColor(254, 243, 199);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(245, 158, 11);
        doc.setLineWidth(0.8);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'S');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('APPOINTMENTS', xPos + cardWidth / 2, yPos + 8, { align: 'center' });
        doc.setFontSize(14);
        doc.setTextColor(245, 158, 11);
        doc.setFont('helvetica', 'bold');
        doc.text(data.totalAppointments.toString(), xPos + cardWidth / 2, yPos + 18, { align: 'center' });
      }
      
      yPos += cardHeight + 15;
    }
    
    // Summary Section
    doc.setFontSize(16);
    doc.setTextColor(HOSPITAL_COLOR);
    doc.setFont('helvetica', 'bold');
    doc.text('Executive Summary', 14, yPos);
    yPos += 10;
    
    // Summary cards in table format
    const summaryData = [];
    if (filters.includePatients) summaryData.push(['Total Patients', data.totalPatients.toString()]);
    if (filters.includeAppointments) summaryData.push(['Total Appointments', data.totalAppointments.toString()]);
    if (filters.includeRevenue) summaryData.push(['Total Revenue', formatCurrency(data.totalRevenue)]);
    if (filters.includeDue) summaryData.push(['Pending Dues', formatCurrency(data.totalDue)]);
    if (filters.includeAppointmentRevenue) summaryData.push(['Appointment Revenue', formatCurrency(data.appointmentRevenue)]);
    if (filters.includeRoomOccupancy) summaryData.push(['Room Occupancy Rate', `${data.roomOccupancyRate}%`]);
    if (filters.includeDischarges) summaryData.push(['Total Discharges', data.totalDischarges.toString()]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { 
        fillColor: [74, 144, 226],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 11
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 10 }
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 15;
    
    // Patients Details
    if (filters.includePatients && data.patients.length > 0) {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }
      
      // Draw section header with background
      doc.setFillColor(74, 144, 226);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Patient Details', 16, yPos);
      yPos += 8;
      
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: yPos,
        head: [['Name', 'Phone', 'Type', 'Registration', 'Total Amount', 'Pending', 'Status']],
        body: data.patients.map(p => [
          p.name,
          p.phone,
          p.patientType,
          p.registrationDate,
          formatCurrency(p.totalPaid),
          formatCurrency(p.totalDue),
          p.status
        ]),
        theme: 'striped',
        headStyles: { 
          fillColor: [74, 144, 226],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },
        margin: { left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 25 },
          2: { cellWidth: 15 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 25, halign: 'right' },
          6: { cellWidth: 20 }
        }
      });
      
      yPos = (doc as any).lastAutoTable.finalY + 5;
      
      // Total below table with styled background showing both paid and due
      doc.setFillColor(240, 248, 255);
      doc.rect(14, yPos - 2, pageWidth - 28, 8, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(16, 185, 129);
      const totalPaid = data.patients.reduce((sum, p) => sum + p.totalPaid, 0);
      const totalDue = data.patients.reduce((sum, p) => sum + p.totalDue, 0);
      const totalCompleted = totalPaid - totalDue;
      doc.text(`Total Amount: ${formatCurrency(totalPaid)}`, 16, yPos + 3);
      doc.setTextColor(245, 158, 11);
      doc.text(`Pending: ${formatCurrency(totalDue)}`, pageWidth / 2 - 15, yPos + 3);
      doc.setTextColor(74, 144, 226);
      doc.text(`Completed: ${formatCurrency(totalCompleted)}`, pageWidth - 75, yPos + 3);
      doc.setTextColor(0, 0, 0);
      yPos += 12;
    }
    
    // Payments/Revenue Details Section
    if ((filters.includeRevenue || filters.includeDue) && data.payments.length > 0) {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }
      
      // Draw section header with background
      doc.setFillColor(74, 144, 226);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment & Revenue Details', 16, yPos);
      yPos += 8;
      
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: yPos,
        head: [['Patient', 'Amount', 'Type', 'Method', 'Date', 'Status', 'Receipt#']],
        body: data.payments.map(p => [
          p.patientName,
          formatCurrency(p.amount),
          p.type,
          p.paymentMethod,
          p.date,
          p.status,
          p.receiptNumber || p.transactionId || '-'
        ]),
        theme: 'striped',
        headStyles: { 
          fillColor: [74, 144, 226],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 7,
          cellPadding: 2
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },
        margin: { left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 22, halign: 'right' },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 24 },
          6: { cellWidth: 23, fontSize: 6 }
        }
      });
      
      yPos = (doc as any).lastAutoTable.finalY + 5;
      
      // Payment summary with colored backgrounds
      const completedPayments = data.payments.filter(p => {
        const status = p.status.toLowerCase();
        return status.includes('paid') || 
               status.includes('fully') || 
               status === 'completed' || 
               status === 'success' || 
               status === 'complete';
      });
      const pendingPayments = data.payments.filter(p => {
        const status = p.status.toLowerCase();
        return status.includes('pending') || 
               status.includes('partial') || 
               status.includes('due') ||
               status === 'unpaid';
      });
      const completedTotal = completedPayments.reduce((sum, p) => sum + p.amount, 0);
      const pendingTotal = pendingPayments.reduce((sum, p) => sum + p.amount, 0);
      
      // Completed revenue
      doc.setFillColor(220, 252, 231);
      doc.rect(14, yPos - 2, (pageWidth - 32) / 2, 8, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(16, 185, 129);
      doc.text(`Completed: ${formatCurrency(completedTotal)}`, 16, yPos + 3);
      
      // Pending dues
      doc.setFillColor(254, 243, 199);
      doc.rect(14 + (pageWidth - 32) / 2 + 4, yPos - 2, (pageWidth - 32) / 2, 8, 'F');
      doc.setTextColor(245, 158, 11);
      doc.text(`Pending: ${formatCurrency(pendingTotal)}`, 18 + (pageWidth - 32) / 2, yPos + 3);
      doc.setTextColor(0, 0, 0);
      yPos += 12;
    }
    
    // Appointments Details
    if (filters.includeAppointments && data.appointments.length > 0) {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }
      
      // Draw section header with background
      doc.setFillColor(74, 144, 226);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Appointment Details', 16, yPos);
      yPos += 8;
      
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: yPos,
        head: [['Patient', 'Doctor', 'Service', 'Date', 'Time', 'Amount', 'Status']],
        body: data.appointments.map(a => [
          a.patientName,
          a.doctorName,
          a.serviceName,
          a.date,
          a.time,
          formatCurrency(a.amount),
          a.status
        ]),
        theme: 'striped',
        headStyles: { 
          fillColor: [74, 144, 226],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },
        margin: { left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 28 },
          2: { cellWidth: 35 },
          3: { cellWidth: 22 },
          4: { cellWidth: 15 },
          5: { cellWidth: 25, halign: 'right' },
          6: { cellWidth: 20 }
        }
      });
      
      yPos = (doc as any).lastAutoTable.finalY + 5;
      
      // Total with styled background
      doc.setFillColor(240, 248, 255);
      doc.rect(14, yPos - 2, pageWidth - 28, 8, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(74, 144, 226);
      doc.text(`Total Appointment Revenue: ${formatCurrency(data.appointmentRevenue)}`, 16, yPos + 3);
      doc.setTextColor(0, 0, 0);
      yPos += 12;
    }
    
    // Discharge Records
    if (filters.includeDischarges && data.discharges.length > 0) {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }
      
      // Draw section header with background
      doc.setFillColor(74, 144, 226);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('Discharge Records', 16, yPos);
      yPos += 8;
      
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: yPos,
        head: [['Patient', 'Admission', 'Discharge', 'Days', 'Total Spent']],
        body: data.discharges.map(d => [
          d.patientName,
          d.admissionDate,
          d.dischargeDate,
          d.totalDays.toString(),
          formatCurrency(d.totalAmountSpent)
        ]),
        theme: 'striped',
        headStyles: { 
          fillColor: [74, 144, 226],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },
        margin: { left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 20, halign: 'center' },
          4: { cellWidth: 35, halign: 'right' }
        }
      });
      
      yPos = (doc as any).lastAutoTable.finalY + 5;
      
      // Total with styled background
      doc.setFillColor(240, 248, 255);
      doc.rect(14, yPos - 2, pageWidth - 28, 8, 'F');
      const totalDischargeRevenue = data.discharges.reduce((sum, d) => sum + d.totalAmountSpent, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(74, 144, 226);
      doc.text(`Total Discharge Revenue: ${formatCurrency(totalDischargeRevenue)}`, 16, yPos + 3);
      doc.setTextColor(0, 0, 0);
    }
    
    // Footer on all pages
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount} | ${HOSPITAL_NAME}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }
    
    // Save PDF
    const filename = `KBR_Report_${formatDateRange(data.startDate, data.endDate).replace(/\//g, '-')}.pdf`;
    doc.save(filename);
    
    console.log('✅ PDF exported successfully');
  } catch (error) {
    console.error('❌ Error generating PDF:', error);
    throw error;
  }
};

// ============================================
// EXCEL EXPORT
// ============================================
export const exportToExcel = async (data: ExportReportData, filters: any) => {
  try {
    console.log('📊 Generating Excel export...');
    
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      [HOSPITAL_NAME],
      ['Hospital Analytics Report'],
      [`Period: ${formatDateRange(data.startDate, data.endDate)}`],
      [`Generated: ${data.generatedAt.toLocaleString('en-GB')}`],
      [],
      ['EXECUTIVE SUMMARY'],
      []
    ];
    
    if (filters.includePatients) summaryData.push(['Total Patients', data.totalPatients.toString()]);
    if (filters.includeAppointments) summaryData.push(['Total Appointments', data.totalAppointments.toString()]);
    if (filters.includeRevenue) summaryData.push(['Total Revenue', data.totalRevenue.toString()]);
    if (filters.includeDue) summaryData.push(['Pending Dues', data.totalDue.toString()]);
    if (filters.includeAppointmentRevenue) summaryData.push(['Appointment Revenue', data.appointmentRevenue.toString()]);
    if (filters.includeRoomOccupancy) summaryData.push(['Room Occupancy Rate', `${data.roomOccupancyRate}%`]);
    if (filters.includeDischarges) summaryData.push(['Total Discharges', data.totalDischarges.toString()]);
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    // Patients Sheet
    if (filters.includePatients && data.patients.length > 0) {
      const patientsData = [
        ['Name', 'Phone', 'Type', 'Registration Date', 'Total Paid', 'Total Due', 'Status'],
        ...data.patients.map(p => [p.name, p.phone, p.patientType, p.registrationDate, p.totalPaid, p.totalDue, p.status])
      ];
      const patientsSheet = XLSX.utils.aoa_to_sheet(patientsData);
      XLSX.utils.book_append_sheet(workbook, patientsSheet, 'Patients');
    }
    
    // Appointments Sheet
    if (filters.includeAppointments && data.appointments.length > 0) {
      const appointmentsData = [
        ['Patient', 'Phone', 'Doctor', 'Department', 'Service', 'Date', 'Time', 'Amount', 'Status', 'Payment Status'],
        ...data.appointments.map(a => [
          a.patientName, 
          a.patientPhone || '-', 
          a.doctorName, 
          a.department || '-', 
          a.serviceName, 
          a.date, 
          a.time, 
          a.amount, 
          a.status, 
          a.paymentStatus || '-'
        ])
      ];
      const appointmentsSheet = XLSX.utils.aoa_to_sheet(appointmentsData);
      XLSX.utils.book_append_sheet(workbook, appointmentsSheet, 'Appointments');
    }
    
    // Payments Sheet
    if ((filters.includeRevenue || filters.includeDue) && data.payments.length > 0) {
      const paymentsData = [
        ['Patient Name', 'Amount', 'Type', 'Payment Method', 'Date', 'Status', 'Receipt Number', 'Transaction ID'],
        ...data.payments.map(p => [
          p.patientName, 
          p.amount, 
          p.type, 
          p.paymentMethod, 
          p.date, 
          p.status, 
          p.receiptNumber || '-', 
          p.transactionId || p.razorpayOrderId || '-'
        ])
      ];
      const paymentsSheet = XLSX.utils.aoa_to_sheet(paymentsData);
      XLSX.utils.book_append_sheet(workbook, paymentsSheet, 'Payments');
    }
    
    // Discharges Sheet
    if (filters.includeDischarges && data.discharges.length > 0) {
      const dischargesData = [
        ['Patient', 'Admission Date', 'Discharge Date', 'Total Days', 'Total Amount Spent'],
        ...data.discharges.map(d => [d.patientName, d.admissionDate, d.dischargeDate, d.totalDays, d.totalAmountSpent])
      ];
      const dischargesSheet = XLSX.utils.aoa_to_sheet(dischargesData);
      XLSX.utils.book_append_sheet(workbook, dischargesSheet, 'Discharges');
    }
    
    // Save Excel file
    const filename = `KBR_Report_${formatDateRange(data.startDate, data.endDate).replace(/\//g, '-')}.xlsx`;
    XLSX.writeFile(workbook, filename);
    
    console.log('✅ Excel exported successfully');
  } catch (error) {
    console.error('❌ Error generating Excel:', error);
    throw error;
  }
};

// ============================================
// WORD EXPORT
// ============================================
export const exportToWord = async (data: ExportReportData, filters: any) => {
  try {
    console.log('📝 Generating Word export...');
    
    const sections: any[] = [];
    
    // Header
    sections.push(
      new Paragraph({
        text: HOSPITAL_NAME,
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 }
      }),
      new Paragraph({
        text: 'Hospital Analytics Report',
        heading: HeadingLevel.HEADING_2,
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 }
      }),
      new Paragraph({
        text: `Period: ${formatDateRange(data.startDate, data.endDate)}`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 50 }
      }),
      new Paragraph({
        text: `Generated: ${data.generatedAt.toLocaleString('en-GB')}`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      }),
      new Paragraph({
        text: 'EXECUTIVE SUMMARY',
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 200 }
      })
    );
    
    // Summary data
    if (filters.includePatients) {
      sections.push(new Paragraph({ text: `Total Patients: ${data.totalPatients}`, spacing: { after: 100 } }));
    }
    if (filters.includeAppointments) {
      sections.push(new Paragraph({ text: `Total Appointments: ${data.totalAppointments}`, spacing: { after: 100 } }));
    }
    if (filters.includeRevenue) {
      sections.push(new Paragraph({ text: `Total Revenue: ${formatCurrency(data.totalRevenue)}`, spacing: { after: 100 } }));
    }
    if (filters.includeDue) {
      sections.push(new Paragraph({ text: `Pending Dues: ${formatCurrency(data.totalDue)}`, spacing: { after: 100 } }));
    }
    if (filters.includeAppointmentRevenue) {
      sections.push(new Paragraph({ text: `Appointment Revenue: ${formatCurrency(data.appointmentRevenue)}`, spacing: { after: 100 } }));
    }
    if (filters.includeRoomOccupancy) {
      sections.push(new Paragraph({ text: `Room Occupancy Rate: ${data.roomOccupancyRate}%`, spacing: { after: 100 } }));
    }
    if (filters.includeDischarges) {
      sections.push(new Paragraph({ text: `Total Discharges: ${data.totalDischarges}`, spacing: { after: 300 } }));
    }
    
    // Patients Details
    if (filters.includePatients && data.patients.length > 0) {
      sections.push(
        new Paragraph({
          text: 'PATIENT DETAILS',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        })
      );
      
      data.patients.forEach(p => {
        sections.push(
          new Paragraph({
            text: `${p.name} - ${p.phone} - ${p.patientType} - Registered: ${p.registrationDate} - Paid: ${formatCurrency(p.totalPaid)}`,
            spacing: { after: 100 }
          })
        );
      });
      
      const totalPaid = data.patients.reduce((sum, p) => sum + p.totalPaid, 0);
      sections.push(
        new Paragraph({
          text: `Total Revenue from Patients: ${formatCurrency(totalPaid)}`,
          spacing: { before: 200, after: 300 }
        })
      );
    }
    
    // Create document
    const doc = new Document({
      sections: [{
        children: sections
      }]
    });
    
    // Save Word file
    const blob = await Packer.toBlob(doc);
    const filename = `KBR_Report_${formatDateRange(data.startDate, data.endDate).replace(/\//g, '-')}.docx`;
    saveAs(blob, filename);
    
    console.log('✅ Word document exported successfully');
  } catch (error) {
    console.error('❌ Error generating Word document:', error);
    throw error;
  }
};
